import { useEffect, useReducer, createContext, useRef, useMemo } from "react";
import AuthReducer from "./AuthReducer";
import getCurrentService from "./GetCurrentService.js"
import { db, auth } from '../firebase';
import { collection, query, where, onSnapshot, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

/**
 * Estado inicial del contexto de autenticación.
 * @type {Object}
 * @property {Object|null} currentUser - Usuario actualmente autenticado.
 * @property {Object|null} currentService - Servicio asociado al usuario actual.
 */
const INITIAL_STATE = {
  currentUser: JSON.parse(localStorage.getItem("user")) || null,
  currentService: JSON.parse(localStorage.getItem("service")) || null,
  adminData: JSON.parse(localStorage.getItem("adminData")) || null,
};

export const AuthContext = createContext(INITIAL_STATE);

/**
 * Proveedor de contexto de autenticación.
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.children - Componentes hijos.
 * @returns {JSX.Element} - Elemento de React que envuelve los componentes hijos con el contexto de autenticación.
 */
export const AuthContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);
  const { currentUser } = state;

  const isMounted = useRef(true); // Referencia para rastrear el montaje del componente

  // Espejo del currentUser accesible desde callbacks no reactivos (listener de
  // onAuthStateChanged), sin re-suscribir el listener en cada cambio de usuario.
  const currentUserRef = useRef(state.currentUser);
  useEffect(() => {
    currentUserRef.current = state.currentUser;
  }, [state.currentUser]);

  useEffect(() => {
    return () => {
      isMounted.current = false; // Se establece a falso cuando el componente se desmonta
    };
  }, []);

  // Reconciliación con la sesión VIVA de Firebase Auth. `currentUser` se
  // hidrata desde localStorage al arrancar y NUNCA se verificaba contra
  // `auth.currentUser`, que es quien firma el ID token de cada httpsCallable.
  // Si divergían —p.ej. una sesión Auth persistida en IndexedDB de otro login
  // en el mismo navegador conviviendo con el localStorage de otra cuenta— la
  // UI mostraba una identidad (y sus datos, porque las lecturas Firestore son
  // públicas) mientras el backend recibía otra, provocando "No es admin del
  // servicio" al crear bajas/incidencias pese a ver al trabajador en pantalla.
  useEffect(() => {
    const limpiarSesion = () => {
      localStorage.removeItem("user");
      localStorage.removeItem("service");
      localStorage.removeItem("adminData");
    };

    const unsubscribe = onAuthStateChanged(auth, (fbUser) => {
      if (!isMounted.current) return;

      // Durante el propio login (Login.jsx) la sesión de Auth cambia ANTES de
      // que se actualice el estado/localStorage; no reconciliar mientras se
      // está en /login para no abortar un inicio de sesión legítimo (incluido
      // el cambio de cuenta, que recarga a "/").
      if (window.location.pathname.startsWith("/login")) return;

      const cached = currentUserRef.current;
      const cachedEmail = (cached?.email || "").toLowerCase();
      const liveEmail = (fbUser?.email || "").toLowerCase();

      if (!fbUser) {
        // No hay sesión Auth viva: cualquier estado de app cacheado es huérfano
        // y sus acciones contra el backend fallarían sin token.
        if (cached) {
          limpiarSesion();
          dispatch({ type: "LOGOUT" });
        }
        return;
      }

      if (!cached) {
        // Sesión Auth viva sin estado de app: adoptar la identidad real.
        dispatch({ type: "LOGIN", payload: fbUser });
        return;
      }

      if (cachedEmail && liveEmail && cachedEmail !== liveEmail) {
        // La identidad que muestra la app no coincide con la que firma el token
        // del backend. Cerramos sesión limpiamente para forzar un re-login
        // coherente en vez de dejar actuar a una identidad fantasma.
        sessionStorage.setItem(
          "authMismatch",
          "Tu sesión se cerró porque no coincidía con tu cuenta. Vuelve a iniciar sesión."
        );
        limpiarSesion();
        auth.signOut().catch(() => {});
        dispatch({ type: "LOGOUT" });
      }
    });

    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCurrentService = async () => {
      if (currentUser) {
        try {
          const currentService = await getCurrentService(currentUser);
          if (isMounted.current) {
            dispatch({ type: "SET_CURRENT_SERVICE", payload: currentService });
            localStorage.setItem("service", JSON.stringify(currentService));
          }
        } catch (error) {
          console.error("Error al obtener el servicio actual:", error);
        }
      }
    };

    fetchCurrentService();
  }, [currentUser]);

  useEffect(() => {
    if (!state.currentUser || !state.currentService) return undefined;

    // Listener EN VIVO sobre el doc admin del usuario: si un administrador
    // cambia los permisos/scope de este usuario (EditAdmin), el cambio se
    // refleja al instante sin re-login. Todo el scope downstream
    // (roleScope/getUserScope, scopeData, serviciosDisponibles y las listas)
    // deriva de adminData, así que basta con actualizarlo aquí.
    const adminQuery = query(
      collection(db, 'servicios', state.currentService.id, 'admins'),
      where('correo', '==', state.currentUser.email),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      adminQuery,
      (adminSnapshot) => {
        if (adminSnapshot.empty || !isMounted.current) return;
        const adminData = adminSnapshot.docs[0].data();
        const adminId = adminSnapshot.docs[0].id;
        const payload = { ...adminData, id: adminId };
        dispatch({ type: "SET_ADMIN_DATA", payload });
        localStorage.setItem("adminData", JSON.stringify(payload));
      },
      (error) => {
        console.error('Error al obtener el perfil:', error);
      }
    );

    return unsubscribe;
  }, [state.currentUser, state.currentService]);

  useEffect(() => {
    localStorage.setItem("user", JSON.stringify(currentUser));
    if (!currentUser) {
      localStorage.removeItem("service");
      localStorage.removeItem("adminData");
    }
  }, [currentUser]);

  const contextValue = useMemo(() => ({
    currentUser: state.currentUser,
    currentService: state.currentService,
    adminData: state.adminData,
    dispatch
  }), [state.currentUser, state.currentService, state.adminData]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

